import React, { useState } from 'react';
import { Grid, Tooltip, Box, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';

import { DataGrid, CommonModal as DetailsModal } from '../../Common';
import { dataGridContainerProps, text_commonCellProps } from '../../Common/DataGrid/utils';
import { StyledCellLink } from '../../Common/DataGrid/CellComponents';
import { getDateRangeFilterOperator } from '../../Common/DataGrid/FilterComponents';
import { Grey2 } from '../../../color_utils';
import Routes from '../../../routes';

const dateRangeFilterOperator = getDateRangeFilterOperator({});

// Read-only email view: links are inert (pointer-events) and base target routes any click into a sandbox-blocked popup.
const emailDoc = (body) => `<!doctype html><html><head><base target="_blank"><style>
    body { font-family: Arial, Helvetica, sans-serif; font-size: 14px; line-height: 1.5; color: #222222; background: #ffffff; margin: 20px; }
    a { color: #1a73e8; pointer-events: none; cursor: default; }
</style></head><body>${body || ''}</body></html>`;

// AB#16929: Alerts Sent report - sent alert emails from dwh.trakrefemails, own instance only.
const AlertsSentTable = () => {
    const [modalRow, setModalRow] = useState(null);

    const columns = [
        {
            field: 'instance',
            headerName: 'Instance',
            filterable: false,
            sortable: false,
        },
        {
            field: 'emailId',
            headerName: 'Email ID',
            ...text_commonCellProps,
        },
        {
            field: 'sentAt',
            headerName: 'Sent At',
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: 'sendTo',
            headerName: 'Recipient Email (To)',
            ...text_commonCellProps,
            width: 200,
        },
        {
            field: 'sendCc',
            headerName: 'Recipient Email (CC)',
            ...text_commonCellProps,
            width: 200,
        },
        {
            field: 'subject',
            headerName: 'Subject',
            ...text_commonCellProps,
            width: 220,
        },
        {
            field: 'region',
            headerName: 'Region',
            ...text_commonCellProps,
        },
        {
            field: 'location',
            headerName: 'Location',
            ...text_commonCellProps,
            renderCell: ({ row: { location, locationId } }) => (
                <StyledCellLink
                    path={`/Location/${locationId}`}
                    text={location}
                    enabled={Boolean(locationId)}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'assetId',
            headerName: 'Asset ID',
            ...text_commonCellProps,
            renderCell: ({ row: { assetId } }) => (
                <StyledCellLink
                    path={`/Inventory/${assetId}`}
                    text={assetId}
                    enabled={Boolean(assetId)}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'assetName',
            headerName: 'Asset Name',
            ...text_commonCellProps,
        },
        {
            field: 'alertType',
            headerName: 'Alert Type',
            ...text_commonCellProps,
            width: 220,
        },
        {
            field: 'tag',
            headerName: 'Tag',
            ...text_commonCellProps,
            width: 220,
        },
        {
            type: 'actions',
            field: 'Quick Actions',
            width: 25,
            getActions: ({ row }) => ([
                <GridActionsCellItem
                    key="view-email"
                    sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                    icon={
                        <Tooltip title="View email">
                            <VisibilityIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    onClick={() => setModalRow(row)}
                />
            ])
        }
    ];

    return (
        <>
            <Grid container>
                <Grid item {...dataGridContainerProps}>
                    <DataGrid
                        defaultSort={'sentAt_desc'}
                        url={Routes.EmailAlerts.AlertsSentTable(false)}
                        exportButtonURL={Routes.EmailAlerts.AlertsSentTable(true)}
                        exportButtonAlt="Export Alerts Sent (.csv)"
                        exportFileName="AlertsSent"
                        noResultText="Oops...It is empty here..."
                        noResultText2="No sent alerts to display"
                        tableId={'alerts-sent-table'}
                        columns={columns}
                        rowCount={10}
                    />
                </Grid>
            </Grid>

            <DetailsModal
                open={Boolean(modalRow)}
                close={() => setModalRow(null)}
                maxWidth={'1100px'}
                width={'95%'}
            >
                <Typography variant="h6" sx={{ mb: 1, pr: 5, wordBreak: 'break-word' }}>
                    {modalRow?.subject}
                </Typography>
                <Box sx={{ mb: 2, color: 'text.secondary', fontSize: 13, lineHeight: 1.7, wordBreak: 'break-word' }}>
                    <div>From: {modalRow?.emailFrom}</div>
                    <div>To: {modalRow?.sendTo}</div>
                    {modalRow?.sendCc ? <div>Cc: {modalRow.sendCc}</div> : null}
                    <div>Sent: {modalRow?.sentAt}</div>
                </Box>
                {/* Sandboxed so the rendered email HTML can't run scripts. */}
                <Box
                    component="iframe"
                    sandbox=""
                    srcDoc={emailDoc(modalRow?.body)}
                    title="Alert email body"
                    sx={{
                        width: '100%',
                        height: '65vh',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: '#fff',
                    }}
                />
            </DetailsModal>
        </>
    );
};

export default AlertsSentTable;
